// The placement rules, pure — what "Smart picks" and "Custom" actually send Meta.
//
// Extracted from the modal because this is the one part of the Ad Creator where a plausible-looking payload
// spends money in the wrong place: an EMPTY positions list is not "no preference", it is Meta AUTOMATIC
// placements, which runs the ad on Audience Network, Marketplace and the Messenger inbox — the three the
// section's own hint copy promises to skip. That distinction has no visible symptom; it shows up as spend.

/** The three-way Smart-picks choice. Mutually exclusive, so "neither platform" is unrepresentable. */
export type SmartPlatform = "both" | "instagram" | "facebook";

export interface AdPlacements {
  publisherPlatforms: string[];
  instagramPositions: string[];
  facebookPositions: string[];
}

/** Feed · Reels · Stories, in Meta's own position vocabulary. IG and FB spell the same three differently. */
export const SMART_INSTAGRAM_POSITIONS = ["stream", "reels", "story"];
export const SMART_FACEBOOK_POSITIONS = ["feed", "facebook_reels", "story"];

/** Which Smart card is lit, DERIVED from the platforms the form holds — never stored beside them, because two
 *  fields for one fact is how a saved draft comes back disagreeing with itself. */
export function smartPlatformOf(platforms: string[] | undefined): SmartPlatform {
  const list = platforms ?? [];
  if (list.length === 1 && list[0] === "instagram") return "instagram";
  if (list.length === 1 && list[0] === "facebook") return "facebook";
  return "both";
}

/** Smart picks: the three positions, spelled out, on the chosen platform(s). */
export function smartPlacements(smart: SmartPlatform): AdPlacements {
  const publisherPlatforms = smart === "both" ? ["facebook", "instagram"] : [smart];
  return {
    publisherPlatforms,
    instagramPositions: publisherPlatforms.includes("instagram") ? [...SMART_INSTAGRAM_POSITIONS] : [],
    facebookPositions: publisherPlatforms.includes("facebook") ? [...SMART_FACEBOOK_POSITIONS] : [],
  };
}

/** Custom: the checked boxes, split per platform. A platform with no checked position is not listed at all —
 *  naming it with an empty positions list would re-open the automatic-placements hole one platform at a time. */
export function customPlacements(checked: string[]): AdPlacements {
  const positions = (platform: string) =>
    checked.filter((k) => k.startsWith(`${platform}:`)).map((k) => k.slice(platform.length + 1));
  const instagramPositions = positions("instagram");
  const facebookPositions = positions("facebook");
  return {
    publisherPlatforms: [
      ...(instagramPositions.length ? ["instagram"] : []),
      ...(facebookPositions.length ? ["facebook"] : []),
    ],
    instagramPositions,
    facebookPositions,
  };
}
